import React, { useState, useContext, useEffect } from "react";
import {
  Typography,
  Button,
  TextField,
  Box,
  Modal,
  Paper,
  Chip,
  Alert,
} from "@mui/material";
import { 
  getFirestore, 
  setDoc, 
  doc, 
  getDoc, 
  updateDoc, 
  query, 
  where, 
  collection, 
  getDocs,
  serverTimestamp,
  onSnapshot
} from 'firebase/firestore';
import { StudentContext } from '../../../../contexts/StudentContext';

// ==================== SUBMISSION SECTION COMPONENT ====================
const SubmissionSection = ({ dayId, courseId }) => {
  const [openModal, setOpenModal] = useState(false);
  const [submissionText, setSubmissionText] = useState("");
  const [submissionFile, setSubmissionFile] = useState(null);
  const [githubRepoUrl, setGithubRepoUrl] = useState("");
  const [commitCode, setCommitCode] = useState("");
  const [submissionNotes, setSubmissionNotes] = useState("");
  const [dayData, setDayData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [existingSubmission, setExistingSubmission] = useState(null);
  const [submissionDocId, setSubmissionDocId] = useState(null);
  const [submissionStatus, setSubmissionStatus] = useState(null);
  const [canResubmit, setCanResubmit] = useState(false);
  const { studentData } = useContext(StudentContext);

  // Real-time listener for submission status changes
  useEffect(() => {
    if (!dayId || !courseId || !studentData?.id) return;

    const db = getFirestore();
    const documentId = `${studentData.id}_${courseId}_${dayId}`;
    const docRef = doc(db, 'answers', documentId);

    const unsubscribe = onSnapshot(docRef, (docSnapshot) => {
      if (docSnapshot.exists()) {
        const data = docSnapshot.data();
        setExistingSubmission(data);
        setSubmissionDocId(docSnapshot.id);
        setSubmissionStatus(data.status || "Pending");
        setCanResubmit(data.status === "Rejected");
        
        // Pre-fill form with existing data
        setGithubRepoUrl(data.githubRepoUrl || "");
        setCommitCode(data.commitCode || "");
        setSubmissionNotes(data.description || data.submissionNotes || "");
      } else {
        setExistingSubmission(null);
        setSubmissionDocId(null);
        setSubmissionStatus(null);
        setCanResubmit(false);
      }
    });

    return () => unsubscribe();
  }, [dayId, courseId, studentData?.id]);

  // Fetch day data and course info
  useEffect(() => {
    const fetchData = async () => {
      if (!dayId || !courseId) return;
      
      setLoading(true);
      try {
        const db = getFirestore();
        
        // Fetch day data
        const dayRef = doc(db, `courses/${courseId}/day/${dayId}`);
        const dayDoc = await getDoc(dayRef);

        if (dayDoc.exists()) {
          setDayData(dayDoc.data());
        }

        // Fetch course title
        const courseRef = doc(db, `courses/${courseId}`);
        const courseDoc = await getDoc(courseRef);
        if (courseDoc.exists()) {
          setDayData(prev => ({ ...prev, courseTitle: courseDoc.data().title }));
        }
        
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [dayId, courseId]);

  const handleFileChange = (event) => {
    setSubmissionFile(event.target.files[0]);
  };

  const handleSubmit = async () => {
    if (!dayData || !studentData?.id) {
      alert('Required data not loaded. Please try again.');
      return;
    }

    const db = getFirestore();
    const studentName = studentData?.name || 'Unknown';
    const courseTitle = dayData?.courseTitle || 'Unknown Course';
    const dayTitle = dayData?.dayTitle || `Day ${dayId}`;
    const documentId = `${studentData.id}_${courseId}_${dayId}`;

    const submissionData = {
      githubRepoUrl,
      commitCode,
      description: submissionNotes, // Match approval page field
      answer: submissionNotes, // Additional field for approval page
      studentName,
      courseTitle,
      dayTitle,
      dayId,
      courseId,
      studentId: studentData.id,
      submittedAt: serverTimestamp(),
    };

    // Handle resubmission case
    if (canResubmit && existingSubmission) {
      submissionData.resubmittedAt = serverTimestamp();
      submissionData.isResubmission = true;
      submissionData.previousStatus = "Rejected";
      // Remove status to make it pending again
      // Don't set status field - let it be undefined/null for "Pending"
    } else if (!existingSubmission) {
      // New submission
      // Don't set status field - let it be undefined/null for "Pending"
    } else {
      // Editing existing submission
      submissionData.updatedAt = serverTimestamp();
    }

    try {
      await setDoc(doc(db, 'answers', documentId), submissionData, { merge: true });
      
      // Also update the course document to sync with approval page logic
      if (dayData) {
        const courseDocRef = doc(db, "courses", courseId, "day", dayId);
        const courseUpdateData = {
          submittedAt: serverTimestamp(),
        };
        
        if (canResubmit && existingSubmission) {
          courseUpdateData.resubmittedAt = serverTimestamp();
          courseUpdateData.isResubmission = true;
        }
        
        await setDoc(courseDocRef, courseUpdateData, { merge: true });
      }
      
      const message = canResubmit ? 'Assignment resubmitted successfully! Status reset to Pending.' 
                                 : existingSubmission ? 'Assignment updated successfully!' 
                                 : 'Assignment submitted successfully!';
      alert(message);
      
      
      
    } catch (error) {
      console.error('Error submitting assignment:', error);
      alert('Failed to submit assignment. Please try again.');
    }

    setOpenModal(false);
  };

  const handleOpenModal = () => {
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    
    // Reset form to existing submission data
    if (existingSubmission) {
      setGithubRepoUrl(existingSubmission.githubRepoUrl || "");
      setCommitCode(existingSubmission.commitCode || "");
      setSubmissionNotes(existingSubmission.description || existingSubmission.submissionNotes || "");
    }
  };

  if (loading) {
    return (
      <div style={{ 
        padding: "32px", 
        textAlign: "center", 
        background: "#fff",
        borderRadius: "16px",
        margin: "24px 0"
      }}>
        <Typography>Loading submission form...</Typography>
      </div>
    );
  }

  return (
    <div>
      {/* Main Submission Box */}
      <SubmissionBox 
        openModal={openModal} 
        setOpenModal={handleOpenModal}
        dayData={dayData}
        dayId={dayId}
        existingSubmission={existingSubmission}
        submissionStatus={submissionStatus}
        canResubmit={canResubmit}
      />

      {/* Submission Modal */}
      <SubmissionModal
        open={openModal}
        onClose={handleCloseModal}
        submissionText={submissionText}
        setSubmissionText={setSubmissionText}
        submissionFile={submissionFile}
        handleFileChange={handleFileChange}
        githubRepoUrl={githubRepoUrl}
        setGithubRepoUrl={setGithubRepoUrl}
        commitCode={commitCode}
        setCommitCode={setCommitCode}
        submissionNotes={submissionNotes}
        setSubmissionNotes={setSubmissionNotes}
        handleSubmit={handleSubmit}
        dayData={dayData}
        dayId={dayId}
        existingSubmission={existingSubmission}
        submissionStatus={submissionStatus}
        canResubmit={canResubmit}
      />
    </div>
  );
};

// ==================== SUBMISSION BOX COMPONENT ====================
const SubmissionBox = ({ 
  openModal, 
  setOpenModal, 
  dayData, 
  dayId, 
  existingSubmission, 
  submissionStatus,
  canResubmit 
}) => {
  
  const getStatusChip = (status) => {
    const statusConfig = {
      "Approved": { color: "#22c55e", bg: "#dcfce7", text: "✅ Approved", border: "#bbf7d0" },
      "Rejected": { color: "#ef4444", bg: "#fef2f2", text: "❌ Rejected", border: "#fecaca" },
      "Pending": { color: "#f59e0b", bg: "#fef3c7", text: "⏳ Pending Review", border: "#fed7aa" },
      "Resubmitted": { color: "#3b82f6", bg: "#dbeafe", text: "🔄 Resubmitted", border: "#bfdbfe" }
    };
    
    const config = statusConfig[status] || statusConfig["Pending"];
    return (
      <Chip 
        label={config.text}
        size="small" 
        style={{ 
          backgroundColor: config.color, 
          color: "white",
          fontWeight: "600"
        }} 
      />
    );
  };

  const getStatusAlert = (status) => {
    if (status === "Rejected") {
      return (
        <Alert severity="error" style={{ marginBottom: "16px" }}>
          <strong>Submission Rejected</strong> - Please review the feedback and resubmit your assignment with corrections.
        </Alert>
      );
    }
    if (status === "Approved") {
      return (
        <Alert severity="success" style={{ marginBottom: "16px" }}>
          <strong>Submission Approved!</strong> - Great work! Your assignment has been approved.
        </Alert>
      );
    }
    return null;
  };

  return (
    <div
      style={{
        background: "#fff",
        borderRadius: "16px",
        boxShadow:
          "0 6px 30px rgba(124, 58, 237, 0.1), 0 2px 12px rgba(0, 0, 0, 0.04)",
        maxWidth: "1000px",
        height: "550px",
        margin: "0 auto",
        border: "1.5px solid #ede9fe",
        transition: "all 0.3s ease-in-out",
        display: "flex",
        flexDirection: "column",
      }}
      onMouseEnter={(e) =>
        (e.currentTarget.style.boxShadow =
          "0 8px 40px rgba(124, 58, 237, 0.15), 0 4px 20px rgba(0, 0, 0, 0.05)")
      }
      onMouseLeave={(e) =>
        (e.currentTarget.style.boxShadow =
          "0 6px 30px rgba(124, 58, 237, 0.1), 0 2px 12px rgba(0, 0, 0, 0.04)")
      }
    >
      {/* Fixed Header */}
      <div style={{
        padding: "32px 28px 16px 28px",
        borderBottom: "1px solid #f1f5f9",
        flexShrink: 0
      }}>
        <Typography
          variant="h5"
          style={{
            fontWeight: "700",
            color: "#4b0082",
            marginBottom: "0",
          }}
        >
          📘 Daily Assignment Submission
        </Typography>
      </div>

      {/* Scrollable Content Area */}
      <div style={{
        flex: 1,
        overflowY: "auto",
        padding: "16px 28px 32px 28px",
        scrollbarWidth: "thin",
        scrollbarColor: "#cbd5e1 #f1f5f9",
      }}>
        {/* Display current day information */}
        {dayData && (
          <div style={{
            background: "linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)",
            padding: "16px 20px",
            borderRadius: "12px",
            marginBottom: "24px",
            border: "1px solid #bae6fd"
          }}>
            <Typography style={{
              fontSize: "16px",
              fontWeight: "600",
              color: "#0369a1",
              marginBottom: "4px"
            }}>
              📅 {dayData.dayTitle || `Day ${dayId}`}
            </Typography>
            <Typography style={{
              fontSize: "14px",
              color: "#0284c7"
            }}>
              Course: {dayData.courseTitle || 'Course'}
            </Typography>
          </div>
        )}

        {/* Status Alert */}
        {submissionStatus && getStatusAlert(submissionStatus)}

        {/* Show submission status */}
        {existingSubmission && (
          <div style={{
            background: submissionStatus === "Approved" 
              ? "linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)"
              : submissionStatus === "Rejected"
              ? "linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)"
              : "linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)",
            padding: "16px 20px",
            borderRadius: "12px",
            marginBottom: "24px",
            border: submissionStatus === "Approved" 
              ? "1px solid #bbf7d0"
              : submissionStatus === "Rejected"
              ? "1px solid #fecaca"
              : "1px solid #fed7aa"
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "8px" }}>
              {getStatusChip(submissionStatus)}
              <Typography style={{
                fontSize: "14px",
                color: submissionStatus === "Approved" ? "#15803d" 
                      : submissionStatus === "Rejected" ? "#dc2626"
                      : "#d97706",
                fontWeight: "500"
              }}>
                Submitted on: {new Date(existingSubmission.submittedAt?.seconds 
                  ? existingSubmission.submittedAt.seconds * 1000 
                  : existingSubmission.submittedAt || existingSubmission.updatedAt).toLocaleDateString()}
              </Typography>
            </div>
            
            {existingSubmission.resubmittedAt && (
              <Typography style={{
                fontSize: "13px",
                color: "#3b82f6",
                fontWeight: "500",
                marginBottom: "8px"
              }}>
                🔄 Resubmitted on: {new Date(existingSubmission.resubmittedAt.seconds 
                  ? existingSubmission.resubmittedAt.seconds * 1000 
                  : existingSubmission.resubmittedAt).toLocaleDateString()}
              </Typography>
            )}
            
            <Typography style={{
              fontSize: "13px",
              color: submissionStatus === "Approved" ? "#15803d" 
                    : submissionStatus === "Rejected" ? "#dc2626"
                    : "#d97706",
              opacity: 0.8,
              wordBreak: "break-all"
            }}>
              GitHub: {existingSubmission.githubRepoUrl}
            </Typography>
            <Typography style={{
              fontSize: "13px",
              color: submissionStatus === "Approved" ? "#15803d" 
                    : submissionStatus === "Rejected" ? "#dc2626"
                    : "#d97706",
              opacity: "0.8",
              wordBreak: "break-all"
            }}>
              Commit: {existingSubmission.commitCode}
            </Typography>
          </div>
        )}

        <Typography
          variant="body1"
          style={{
            color: "#374151",
            marginBottom: "24px",
            fontSize: "16px",
            lineHeight: "1.8",
          }}
        >
          <strong style={{ fontSize: "17px", color: "#6b21a8" }}>
            📌 Follow these steps:
          </strong>
          <ul
            style={{ margin: "20px 0 0 20px", padding: 0, listStyleType: "none" }}
          >
            <li style={{ marginBottom: "14px" }}>
              ✅ Create a <strong>public GitHub repo</strong> named: {" "}
              <em>Assignment_Day_&lt;DayNumber&gt;</em> (e.g., {" "}
              <code>Assignment_Day_{dayId}</code>).
            </li>
            <li style={{ marginBottom: "14px" }}>
              📂 Upload <strong>all assignments for the day</strong> to that repo.
            </li>
            <li style={{ marginBottom: "14px" }}>
              🏷️ Label each file properly (e.g., <code>Q1.py</code>, {" "}
              <code>Q2.html</code>).
            </li>
            <li style={{ marginBottom: "14px" }}>
              📝 Make a commit with a clear message like {" "}
              <em>"Added Day {dayId} assignments"</em>.
            </li>
            <li style={{ marginBottom: "14px" }}>
              ☁️ Push everything to GitHub and ensure the repo is {" "}
              <strong>public</strong>.
            </li>
            <li style={{ marginBottom: "14px" }}>
              🔗 {canResubmit ? 'Resubmit your assignment:' 
                  : existingSubmission ? 'Update your submission:' 
                  : 'Submit the following:'}
              <Button
                variant="contained"
                onClick={() => setOpenModal(true)}
                disabled={submissionStatus === "Approved"}
                style={{
                  backgroundColor: canResubmit ? "#ef4444" 
                                  : existingSubmission ? "#f59e0b" 
                                  : "#6b21a8",
                  color: "#fff",
                  textTransform: "none",
                  padding: "12px 28px",
                  fontSize: "15px",
                  borderRadius: "10px",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                  marginTop: "20px",
                  marginLeft: "20px",
                  opacity: submissionStatus === "Approved" ? 0.5 : 1,
                }}
              >
                {canResubmit ? "🔄 Resubmit Assignment" 
                : existingSubmission ? "✏️ Update Submission" 
                : "🚀 Open Submission Modal"}
              </Button>
            </li>
          </ul>
        </Typography>
      </div>
    </div>
  );
};

// ==================== SUBMISSION MODAL COMPONENT ====================
const SubmissionModal = ({
  open,
  onClose,
  githubRepoUrl,
  setGithubRepoUrl,
  commitCode,
  setCommitCode,
  submissionNotes,
  setSubmissionNotes,
  handleSubmit,
  dayData,
  dayId,
  existingSubmission,
  submissionStatus,
  canResubmit,
}) => (
  <Modal
    open={open}
    onClose={onClose}
    style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }}
  >
    <Paper
      style={{
        width: "90%",
        maxWidth: "600px",
        maxHeight: "90vh",
        overflowY: "auto",
        padding: "32px",
        borderRadius: "16px",
        boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
        background: "linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)",
      }}
    >
      <Typography
        variant="h4"
        style={{
          fontWeight: "700",
          color: "#4b0082",
          marginBottom: "16px",
          textAlign: "center",
        }}
      >
        {canResubmit ? "🔄 Resubmit Assignment" 
        : existingSubmission ? "✏️ Update Assignment" 
        : "📤 Submit Assignment"}
      </Typography>

      {/* Display submission context */}
      {dayData && (
        <div style={{
          background: canResubmit ? "rgba(239, 68, 68, 0.1)" 
                    : existingSubmission ? "rgba(245, 158, 11, 0.1)"
                    : "rgba(107, 33, 168, 0.1)",
          padding: "16px",
          borderRadius: "12px",
          marginBottom: "24px",
          textAlign: "center"
        }}>
          <Typography style={{
            fontSize: "16px",
            fontWeight: "600",
            color: canResubmit ? "#dc2626" 
                  : existingSubmission ? "#d97706"
                  : "#6b21a8"
          }}>
            {canResubmit ? 'Resubmitting for:' 
            : existingSubmission ? 'Updating submission for:' 
            : 'Submitting for:'} {dayData.dayTitle || `Day ${dayId}`}
          </Typography>
          <Typography style={{
            fontSize: "14px", 
            color: canResubmit ? "#dc2626" 
                  : existingSubmission ? "#d97706"
                  : "#6b21a8",
            opacity: 0.8
          }}>
            {dayData.courseTitle || 'Course'}
          </Typography>
          {existingSubmission && (
            <Typography style={{
              fontSize: "12px",
              color: canResubmit ? "#dc2626" 
                    : existingSubmission ? "#d97706"
                    : "#6b21a8",
              opacity: 0.6,
              marginTop: "4px"
            }}>
              Status: {submissionStatus || "Pending"} | Originally submitted: {new Date(existingSubmission.submittedAt?.seconds 
                ? existingSubmission.submittedAt.seconds * 1000 
                : existingSubmission.submittedAt || existingSubmission.updatedAt).toLocaleString()}
            </Typography>
          )}
        </div>
      )}

      {canResubmit && (
        <Alert severity="warning" style={{ marginBottom: "24px" }}>
          <strong>Resubmission Notice:</strong> Your previous submission was rejected. 
          Please make the necessary corrections and resubmit. This will reset your status to "Pending" for review.
        </Alert>
      )}

      <Box style={{ marginBottom: "24px" }}>
        <Typography
          variant="h6"
          style={{
            fontWeight: "600",
            color: "#374151",
            marginBottom: "12px",
          }}
        >
          🔗 GitHub Repository URL *
        </Typography>
        <TextField
          fullWidth
          variant="outlined"
          placeholder={`https://github.com/username/Assignment_Day_${dayId}`}
          value={githubRepoUrl}
          onChange={(e) => setGithubRepoUrl(e.target.value)}
          style={{ marginBottom: "16px" }}
          InputProps={{
            style: {
              borderRadius: "12px",
              backgroundColor: "#ffffff",
            },
          }}
        />
      </Box>

      <Box style={{ marginBottom: "24px" }}>
        <Typography
          variant="h6"
          style={{
            fontWeight: "600",
            color: "#374151",
            marginBottom: "12px",
          }}
        >
          🏷️ Commit Hash/Code *
        </Typography>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="e.g., abc123def or latest commit hash"
          value={commitCode}
          onChange={(e) => setCommitCode(e.target.value)}
          style={{ marginBottom: "16px" }}
          InputProps={{
            style: {
              borderRadius: "12px",
              backgroundColor: "#ffffff",
            },
          }}
        />
        <Typography
          variant="caption"
          style={{
            color: "#6b7280",
            fontSize: "12px",
            fontStyle: "italic",
          }}
        >
          💡 Tip: You can find the commit hash on GitHub in your repository's
          commit history
        </Typography>
      </Box>

      <Box style={{ marginBottom: "24px" }}>
        <Typography
          variant="h6"
          style={{
            fontWeight: "600",
            color: "#374151",
            marginBottom: "12px",
          }}
        >
          📝 Description/Notes *
        </Typography>
        <TextField
          fullWidth
          multiline
          rows={3}
          variant="outlined"
          placeholder="Describe your assignment solution, any challenges faced, or additional notes..."
          value={submissionNotes}
          onChange={(e) => setSubmissionNotes(e.target.value)}
          InputProps={{
            style: {
              borderRadius: "12px",
              backgroundColor: "#ffffff",
            },
          }}
        />
        <Typography
          variant="caption"
          style={{
            color: "#6b7280",
            fontSize: "12px",
            fontStyle: "italic",
            marginTop: "4px",
            display: "block"
          }}
        >
          This field is required and will be visible to reviewers in the approval dashboard.
        </Typography>
      </Box>

      <Box
        style={{
          display: "flex",
          gap: "16px",
          justifyContent: "flex-end",
          marginTop: "32px",
        }}
      >
        <Button
          onClick={onClose}
          variant="outlined"
          style={{
            padding: "12px 24px",
            borderRadius: "10px",
            textTransform: "none",
            borderColor: "#d1d5db",
            color: "#374151",
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!githubRepoUrl.trim() || !commitCode.trim() || !submissionNotes.trim()}
          style={{
            backgroundColor:
              githubRepoUrl.trim() && commitCode.trim() && submissionNotes.trim()
                ? (canResubmit ? "#ef4444" 
                  : existingSubmission ? "#f59e0b" 
                  : "#6b21a8") 
                : "#9ca3af",
            color: "#fff",
            padding: "12px 24px",
            borderRadius: "10px",
            textTransform: "none",
            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
          }}
        >
          {canResubmit ? "🔄 Resubmit Assignment" 
          : existingSubmission ? "💾 Update Assignment" 
          : "🚀 Submit Assignment"}
        </Button>
      </Box>
    </Paper>
  </Modal>
);

export default SubmissionSection;